import {tokenStore} from '../apollo/tokens'

/**
 * Auth/session persistence. Tokens live in `tokenStore` (localStorage) so the
 * session survives reloads and offline periods. The access token (short-lived)
 * is sent as a Bearer header; the refresh token (long-lived) is exchanged for a
 * fresh pair on reconnect / on an UNAUTHENTICATED error.
 */

export const GRAPHQL_URI = import.meta.env.VITE_GRAPHQL_URI ?? '/graph'

const REFRESH_MUTATION =
  'mutation($t: String!){ refreshToken(refreshToken: $t){ success accessToken refreshToken } }'

/** Persist the access + refresh tokens returned by login / signup / refresh. */
export function storeAuthTokens(payload: any): void {
  if (payload?.accessToken && payload?.refreshToken) {
    tokenStore.set({accessToken: payload.accessToken, refreshToken: payload.refreshToken})
  }
}

export function clearAuthTokens(): void {
  tokenStore.clear()
}

let inFlight: Promise<boolean> | null = null

/**
 * Exchange the persisted refresh token for a fresh access+refresh pair (rotation).
 * No-op offline or without a refresh token. Concurrent callers share one request.
 * Returns whether a new access token is now available.
 */
export function refreshAccessToken(): Promise<boolean> {
  if (inFlight) return inFlight
  inFlight = (async () => {
    const refresh = tokenStore.refresh
    if (!refresh || (typeof navigator !== 'undefined' && !navigator.onLine)) return false
    try {
      const res = await fetch(GRAPHQL_URI, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({query: REFRESH_MUTATION, variables: {t: refresh}}),
      })
      const json = await res.json()
      const payload = json?.data?.refreshToken
      if (payload?.accessToken && payload?.refreshToken) {
        tokenStore.set({accessToken: payload.accessToken, refreshToken: payload.refreshToken})
        return true
      }
      // Refresh token rejected (expired/revoked) — drop the dead session.
      tokenStore.clear()
      return false
    } catch {
      return false // network error: keep the tokens, try again later
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}
